import type { PhotoPolicy, PunchRequest, PunchResult, PunchSyncResult } from '@worklogix/shared';
import { ApiError } from '@worklogix/web-common';

import { getCurrent, syncPunches, uploadMissingPhoto, uploadStoredPhoto } from '../api';
import { getDeviceContext, setDeviceContext } from '../device/deviceToken';
import {
  enqueuePhoto,
  getAllPhotos,
  getPhotoViews,
  subscribePhotoWarnings,
  updatePhoto,
  type QueuedPhoto,
} from '../device/photoQueue';
import {
  countPending,
  enqueue,
  getAll,
  getPending,
  subscribeQueue,
  update,
  type QueuedPunch,
} from '../device/queue';
import {
  mapCaptureErrorToReason,
  shouldWarnRequiredPhoto,
  type CaptureOutcome,
  type PhotoCapturer,
} from '../photo/captureReason';
import { selectUploadablePhotos } from '../photo/photoDrain';
import type { KioskCurrentEntry, PunchEcho } from '../types';

import { sortEntriesByRecent } from './projection';

/** Punch statuses that mean the `kiosk_punch` row exists server-side (FK-satisfiable). */
const SYNCED_PUNCH_STATUSES: ReadonlySet<QueuedPunch['status']> = new Set(['applied', 'duplicate']);

/**
 * Kiosk sync engine — the online/offline drain driver + reconciliation.
 *
 * Offline-first: every punch is enqueued durably (queue.ts) FIRST; this engine
 * drains the queue to `POST /kiosk/punch/sync` in punchedAt order whenever the
 * device is online (navigator.onLine + the 'online' event) and on a periodic
 * retry with backoff. The server is authoritative — applied/duplicate => synced;
 * a transient failure => stays pending and retries.
 *
 * The named clocked-in list comes ENTIRELY from `GET /kiosk/current` (only the
 * server can resolve PINs to people) and is refreshed after each successful
 * drain. Offline, a queued punch shows a generic "recorded — confirming…" echo
 * with no name; the authoritative name + kind appear once the sync response lands.
 */

export interface KioskState {
  /** navigator.onLine snapshot. */
  online: boolean;
  /** True while a drain is in flight. */
  syncing: boolean;
  /** Not-yet-synced punch count (queued-count badge). */
  queuedCount: number;
  /** Department banner text, once known. */
  departmentName: string | null;
  /** Currently clocked-in employees for this department (server-authoritative). */
  clockedIn: KioskCurrentEntry[];
  /** Latest punch outcome for the operator. */
  echo: PunchEcho | null;
  /** False after a failed server round-trip (drives the offline/retry banner). */
  serverReachable: boolean;
  /** Tenant photo-on-punch enable flag (from `GET /kiosk/current`). */
  photoEnabled: boolean;
  /** Tenant capture policy; `required` drives the non-blocking camera warning. */
  photoPolicy: PhotoPolicy;
  /**
   * Transient, NON-BLOCKING photo warning (e.g. required-but-missing, or a
   * storage-quota notice). Never gates the clock — purely advisory.
   */
  photoWarning: string | null;
}

const INITIAL_STATE: KioskState = {
  online: typeof navigator === 'undefined' ? true : navigator.onLine,
  syncing: false,
  queuedCount: 0,
  departmentName: getDeviceContext()?.departmentName ?? null,
  clockedIn: [],
  echo: null,
  serverReachable: true,
  photoEnabled: false,
  photoPolicy: 'optional',
  photoWarning: null,
};

const RETRY_INTERVAL_MS = 15_000;
const BACKOFF_BASE_MS = 2_000;
const BACKOFF_CAP_MS = 30_000;
/**
 * Consecutive failed server round-trips before the "Offline" badge shows. The
 * kiosk re-probes the server every cycle, so on flaky wifi a lone probe often
 * blips; showing Offline on the FIRST failure made the badge flicker
 * Syncing→Offline→Online. Only a sustained failure (this many in a row, with any
 * success resetting the streak) flips the badge — the clock is usable throughout.
 */
const OFFLINE_AFTER_FAILURES = 3;
/**
 * A drain can't legitimately run this long. If `syncing` has been true longer than
 * this, treat it as a STUCK flag (a drain interrupted when iOS paused/killed the
 * PWA mid-await, so its `finally` never ran) and drain anyway — otherwise the
 * queue freezes forever while the kiosk still shows Online. A re-sent punch is
 * idempotent server-side, so re-draining a maybe-in-flight batch is safe.
 */
const STALE_SYNC_MS = 30_000;
/** Successful-sync responses that omit a queued punch before we treat it as rejected. */
const MAX_ABSENT_BEFORE_REJECT = 2;
const ECHO_AUTO_DISMISS_MS = 8_000;
const PHOTO_WARNING_DISMISS_MS = 8_000;

function kindLabel(kind: PunchResult['kind']): string {
  switch (kind) {
    case 'clocked_in':
      return 'Clocked in';
    case 'clocked_out':
      return 'Clocked out';
    case 'switched':
      return 'Switched department';
    default:
      return 'Recorded';
  }
}

class KioskEngine {
  private state: KioskState = INITIAL_STATE;
  private readonly listeners = new Set<() => void>();

  private lastSubmittedKey: string | null = null;
  private failureCount = 0;
  /** Consecutive failed server round-trips (drives the debounced Offline badge). */
  private serverFailureStreak = 0;
  /** Epoch ms when the current drain set `syncing` true; null when idle. Drives the
   * stale-sync watchdog so a drain interrupted mid-await can't freeze the queue. */
  private syncingSince: number | null = null;
  private retryTimer: number | null = null;
  private echoTimer: number | null = null;
  private photoWarningTimer: number | null = null;
  private drainingPhotos = false;
  private unsubscribeQueue: (() => void) | null = null;
  private unsubscribePhotoWarnings: (() => void) | null = null;
  private started = false;

  getState = (): KioskState => this.state;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * A successful round-trip: clear the failure streak and (only if it changed)
   * flip the badge back to reachable — no redundant re-render when already online.
   */
  private markServerReachable(): void {
    this.serverFailureStreak = 0;
    if (!this.state.serverReachable) {
      this.setState({ serverReachable: true });
    }
  }

  /**
   * A failed round-trip: only flip to unreachable after OFFLINE_AFTER_FAILURES in
   * a row, so a lone transient blip never shows Offline (debounce / hysteresis).
   */
  private markServerUnreachable(): void {
    this.serverFailureStreak += 1;
    if (this.serverFailureStreak >= OFFLINE_AFTER_FAILURES && this.state.serverReachable) {
      this.setState({ serverReachable: false });
    }
  }

  private setState(patch: Partial<KioskState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) {
      listener();
    }
  }

  private readonly onOnline = (): void => {
    this.setState({ online: true });
    void this.drainCycle();
  };

  private readonly onOffline = (): void => {
    this.setState({ online: false });
  };

  // iOS throttles/pauses timers for a backgrounded PWA; when the operator returns
  // to the app, kick a drain immediately so a punch made just before backgrounding
  // (or left queued) syncs without waiting for the next retry tick.
  private readonly onVisible = (): void => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      void this.drainCycle();
    }
  };

  start(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    // Clear transient in-flight flags: a drain interrupted at unmount / an iOS PWA
    // resume can leave these stuck true, which freezes the queue behind the guards.
    this.setState({ syncing: false });
    this.syncingSince = null;
    this.drainingPhotos = false;
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('visibilitychange', this.onVisible);
    this.unsubscribeQueue = subscribeQueue(() => {
      void this.refreshQueuedCount();
    });
    this.unsubscribePhotoWarnings = subscribePhotoWarnings((warning) => {
      if (warning !== null) {
        this.setPhotoWarning(warning);
      }
    });
    this.retryTimer = window.setInterval(() => {
      void this.drainCycle();
    }, RETRY_INTERVAL_MS);

    void this.refreshQueuedCount();
    void this.refreshCurrent();
    void this.drainCycle();
  }

  stop(): void {
    this.started = false;
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('visibilitychange', this.onVisible);
    if (this.retryTimer !== null) {
      window.clearInterval(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.echoTimer !== null) {
      window.clearTimeout(this.echoTimer);
      this.echoTimer = null;
    }
    if (this.photoWarningTimer !== null) {
      window.clearTimeout(this.photoWarningTimer);
      this.photoWarningTimer = null;
    }
    this.unsubscribeQueue?.();
    this.unsubscribeQueue = null;
    this.unsubscribePhotoWarnings?.();
    this.unsubscribePhotoWarnings = null;
  }

  /**
   * Record a punch: enqueue durably, capture+enqueue the verification photo (when
   * enabled), echo optimistically, then try to drain.
   *
   * The `capture` capturer is supplied by the clock screen (DOM edge) ONLY when
   * `photoEnabled`. Capture is best-effort and NEVER blocks the punch: the punch
   * is enqueued FIRST and unconditionally; a failed/absent capture is recorded as
   * a `missing` photo, and `required` policy adds a non-blocking warning.
   */
  async submitPin(pin: string, capture?: PhotoCapturer): Promise<void> {
    const context = getDeviceContext();
    const now = new Date().toISOString();
    const idempotencyKey = crypto.randomUUID();
    const record: QueuedPunch = {
      idempotencyKey,
      pin,
      departmentId: context?.departmentId ?? null,
      punchedAt: now,
      syncState: 'pending',
      attempts: 0,
      createdAt: now,
      employeeName: null,
      kind: null,
      status: null,
    };
    this.lastSubmittedKey = idempotencyKey;
    // Punch is durable BEFORE any photo work — the photo can never delay it.
    await enqueue(record);
    this.setEcho({
      pending: true,
      rejected: false,
      kind: null,
      employeeName: null,
      message: 'Punch recorded — confirming…',
      at: Date.now(),
    });

    if (this.state.photoEnabled && capture !== undefined) {
      await this.capturePhoto(idempotencyKey, capture);
    }

    await this.drainCycle();
  }

  /**
   * Capture + enqueue the verification photo for a just-recorded punch. Total by
   * construction: the capturer resolves `missing` on failure, and an unexpected
   * throw is mapped to a `missing` record too. When the tenant policy is
   * `required` and the photo is missing, raise a conspicuous but NON-BLOCKING
   * warning. Never re-throws — the punch is already durable.
   */
  private async capturePhoto(idempotencyKey: string, capture: PhotoCapturer): Promise<void> {
    let outcome: CaptureOutcome;
    try {
      outcome = await capture();
    } catch (error) {
      outcome = {
        status: 'missing',
        blob: null,
        missingReason: mapCaptureErrorToReason(error),
        capturedAt: new Date().toISOString(),
      };
    }

    const photo: QueuedPhoto = {
      idempotencyKey,
      blob: outcome.blob,
      status: outcome.status,
      capturedAt: outcome.capturedAt,
      photoSynced: false,
      attempts: 0,
      ...(outcome.missingReason !== undefined ? { missingReason: outcome.missingReason } : {}),
    };
    try {
      await enqueuePhoto(photo);
    } catch {
      // Persisting the photo failed (non-quota). The punch still stands; the photo
      // is simply lost — acceptable under allow+flag. Do not surface as a blocker.
    }

    if (shouldWarnRequiredPhoto(this.state.photoPolicy, outcome.status)) {
      this.setPhotoWarning('Photo required — please face the camera.');
    }
  }

  clearEcho(): void {
    if (this.echoTimer !== null) {
      window.clearTimeout(this.echoTimer);
      this.echoTimer = null;
    }
    this.setState({ echo: null });
  }

  private setEcho(echo: PunchEcho): void {
    if (this.echoTimer !== null) {
      window.clearTimeout(this.echoTimer);
      this.echoTimer = null;
    }
    this.setState({ echo });
    if (!echo.pending) {
      this.echoTimer = window.setTimeout(() => {
        this.setState({ echo: null });
        this.echoTimer = null;
      }, ECHO_AUTO_DISMISS_MS);
    }
  }

  private setPhotoWarning(message: string): void {
    if (this.photoWarningTimer !== null) {
      window.clearTimeout(this.photoWarningTimer);
      this.photoWarningTimer = null;
    }
    this.setState({ photoWarning: message });
    this.photoWarningTimer = window.setTimeout(() => {
      this.setState({ photoWarning: null });
      this.photoWarningTimer = null;
    }, PHOTO_WARNING_DISMISS_MS);
  }

  private async refreshQueuedCount(): Promise<void> {
    const queuedCount = await countPending();
    this.setState({ queuedCount });
  }

  private async refreshCurrent(): Promise<void> {
    if (!this.state.online) {
      return;
    }
    try {
      const current = await getCurrent();
      setDeviceContext({
        departmentId: current.departmentId,
        departmentName: current.departmentName,
      });
      this.setState({
        departmentName: current.departmentName,
        clockedIn: sortEntriesByRecent(current.sessions),
        photoEnabled: current.photoEnabled,
        photoPolicy: current.photoPolicy,
      });
      this.markServerReachable();
    } catch (err) {
      // A 401 is an AUTH failure, not a connectivity one — the server IS reachable,
      // so reporting "offline" is wrong (and, on an idle kiosk, sticky). Leave
      // reachability untouched; api.ts escalates repeated 401s to an un-pair, which
      // drops the device to the pairing screen rather than a false Offline badge.
      if (err instanceof ApiError && err.status === 401) {
        return;
      }
      // Genuine transport failure: only flags Offline after several in a row.
      this.markServerUnreachable();
    }
  }

  private scheduleRetry(): void {
    const delay = Math.min(BACKOFF_BASE_MS * 2 ** this.failureCount, BACKOFF_CAP_MS);
    window.setTimeout(() => {
      void this.drainCycle();
    }, delay);
  }

  /**
   * One full drain cycle: punches FIRST (so their `kiosk_punch.id` exists), then
   * photos (whose upload FK depends on that id). Photos drain even when there are
   * no pending punches — the two stores drain independently.
   */
  private async drainCycle(): Promise<void> {
    await this.drainPunches();
    await this.drainPhotos();
    // Re-probe the server every cycle (the retry timer + online event drive this),
    // so an IDLE kiosk recovers from a transient "offline" instead of sticking on
    // it until the next punch — and a stale-token 401 surfaces to the un-pair path.
    await this.refreshCurrent();
  }

  /**
   * SECOND drain: upload photos whose punch has already synced (FK-satisfiable).
   * Pure `selectUploadablePhotos` decides eligibility + order; a 409 means the
   * punch hasn't landed yet (retry next cycle); a transient error bumps attempts.
   * Never re-uploads a `photoSynced` photo. A photo failure NEVER affects a punch.
   */
  private async drainPhotos(): Promise<void> {
    if (this.drainingPhotos || !this.state.online) {
      return;
    }
    const views = await getPhotoViews();
    const punches = await getAll();
    const syncedKeys = new Set(
      punches.filter((p) => SYNCED_PUNCH_STATUSES.has(p.status)).map((p) => p.idempotencyKey)
    );
    const uploadable = selectUploadablePhotos(views, syncedKeys);
    if (uploadable.length === 0) {
      return;
    }

    this.drainingPhotos = true;
    try {
      const byKey = new Map((await getAllPhotos()).map((p) => [p.idempotencyKey, p]));
      for (const view of uploadable) {
        const photo = byKey.get(view.idempotencyKey);
        if (photo !== undefined) {
          await this.uploadOnePhoto(photo);
        }
      }
    } finally {
      this.drainingPhotos = false;
    }
  }

  private async uploadOnePhoto(photo: QueuedPhoto): Promise<void> {
    try {
      if (photo.status === 'stored' && photo.blob !== null) {
        await uploadStoredPhoto(photo.idempotencyKey, photo.blob);
      } else {
        await uploadMissingPhoto(photo.idempotencyKey, photo.missingReason ?? 'capture_failed');
      }
      // 2xx: mark synced + drop the blob so it stops consuming quota.
      await updatePhoto(photo.idempotencyKey, {
        photoSynced: true,
        blob: null,
        attempts: photo.attempts + 1,
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        // Punch not yet on the server — leave untouched, retry after next punch
        // drain. Do NOT bump attempts (it isn't a real failure).
        return;
      }
      // Transient transport/5xx: bump attempts, retry with the next cycle.
      await updatePhoto(photo.idempotencyKey, { attempts: photo.attempts + 1 });
    }
  }

  private async drainPunches(): Promise<void> {
    if (!this.state.online) {
      return;
    }
    // Respect an in-flight drain — but only if it's RECENT. A `syncing` flag older
    // than STALE_SYNC_MS is stuck (its drain was killed mid-await and never cleared
    // the flag), so fall through and drain rather than freeze the queue forever.
    if (this.state.syncing) {
      const inFlightMs = this.syncingSince === null ? 0 : Date.now() - this.syncingSince;
      if (inFlightMs < STALE_SYNC_MS) {
        return;
      }
    }
    const pending = (await getPending()).filter((p) => p.pin !== null);
    if (pending.length === 0) {
      return;
    }

    this.setState({ syncing: true });
    this.syncingSince = Date.now();
    try {
      const requests: PunchRequest[] = pending.map((p) => ({
        pin: p.pin as string,
        idempotencyKey: p.idempotencyKey,
        punchedAt: p.punchedAt,
      }));

      const results = await syncPunches(requests);
      const byKey = new Map<string, PunchSyncResult>(results.map((r) => [r.idempotencyKey, r]));

      for (const item of pending) {
        const result = byKey.get(item.idempotencyKey);
        if (result !== undefined) {
          await this.applyResult(item, result);
        } else {
          await this.handleAbsent(item);
        }
      }

      this.failureCount = 0;
      this.markServerReachable();
      // The clocked-in list is refreshed by drainCycle's refreshCurrent right after
      // this returns, so no separate call is needed here.
    } catch (err) {
      // A 401 is an auth failure (stale token), not connectivity — don't flag the
      // server unreachable; api.ts escalates repeated 401s to an un-pair. Only a
      // real transport failure (network / 5xx) means "offline".
      if (!(err instanceof ApiError && err.status === 401)) {
        this.failureCount += 1;
        this.markServerUnreachable();
      }
      // Do NOT penalize per-item attempt counts for a transport failure — retry
      // with backoff (a persistent 401 escalates to the pairing screen).
      this.scheduleRetry();
    } finally {
      this.setState({ syncing: false });
      this.syncingSince = null;
      await this.refreshQueuedCount();
    }
  }

  private async applyResult(item: QueuedPunch, result: PunchSyncResult): Promise<void> {
    const employeeName = result.result.employeeName;
    await update(item.idempotencyKey, {
      syncState: 'synced',
      pin: null,
      status: result.status,
      kind: result.result.kind,
      employeeName,
      attempts: item.attempts + 1,
    });

    if (item.idempotencyKey === this.lastSubmittedKey) {
      this.setEcho({
        pending: false,
        rejected: false,
        kind: result.result.kind,
        employeeName,
        message: `${employeeName} — ${kindLabel(result.result.kind)}`,
        at: Date.now(),
      });
    }
  }

  private async handleAbsent(item: QueuedPunch): Promise<void> {
    const attempts = item.attempts + 1;
    if (attempts >= MAX_ABSENT_BEFORE_REJECT) {
      // Consistently rejected by an authoritative 2xx response — almost always an
      // unrecognized PIN. Give up, clear the PIN, surface it.
      await update(item.idempotencyKey, { syncState: 'error', pin: null, attempts });
      if (item.idempotencyKey === this.lastSubmittedKey) {
        this.setEcho({
          pending: false,
          rejected: true,
          kind: null,
          employeeName: null,
          message: 'PIN not recognized. Please try again.',
          at: Date.now(),
        });
      }
    } else {
      await update(item.idempotencyKey, { attempts });
    }
  }
}

/** Process-wide singleton — the whole kiosk shares one queue + drain loop. */
export const kioskEngine = new KioskEngine();
